import { Link, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog'

interface SystemSecret {
  id: number
  key: string
  value: string
  created_at: string
  updated_at: string
  dev_instance: {
    id: number
    name: string
  }
}

interface PagyMetadata {
  page: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface AdminSystemSecretsIndexProps {
  system_secrets: SystemSecret[]
  pagy: PagyMetadata
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function Index({ system_secrets, pagy }: AdminSystemSecretsIndexProps) {
  const { t } = useTranslation('admin')
  const [visibleValues, setVisibleValues] = useState<Set<number>>(new Set())
  const [bulkAction, setBulkAction] = useState<'set' | 'delete'>('set')
  const [bulkKey, setBulkKey] = useState('')
  const [bulkValue, setBulkValue] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const toggleValueVisibility = (id: number) => {
    setVisibleValues(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleBulkAction = (e: React.FormEvent) => {
    e.preventDefault()

    if (!bulkKey.trim()) {
      return
    }

    const confirmMessage = bulkAction === 'set'
      ? t('systemSecretsIndex.dialog.confirmSet', { key: bulkKey, value: bulkValue })
      : t('systemSecretsIndex.dialog.confirmDelete', { key: bulkKey })

    if (!confirm(confirmMessage)) {
      return
    }

    if (bulkAction === 'set') {
      router.post('/admin/system_secrets/bulk_set', {
        key: bulkKey,
        value: bulkValue
      }, {
        onSuccess: () => {
          setBulkKey('')
          setBulkValue('')
          setIsDialogOpen(false)
        }
      })
    } else {
      router.delete(`/admin/system_secrets/bulk_delete?key=${encodeURIComponent(bulkKey)}`, {
        onSuccess: () => {
          setBulkKey('')
          setIsDialogOpen(false)
        }
      })
    }
  }

  // Group secrets by dev instance
  const groupedSecrets = system_secrets.reduce((acc, secret) => {
    const instanceId = secret.dev_instance.id
    if (!acc[instanceId]) {
      acc[instanceId] = {
        dev_instance: secret.dev_instance,
        secrets: []
      }
    }
    acc[instanceId].secrets.push(secret)
    return acc
  }, {} as Record<number, { dev_instance: { id: number; name: string }; secrets: SystemSecret[] }>)

  const devInstances = Object.values(groupedSecrets)

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 py-2">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{t('systemSecretsIndex.title')}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('systemSecretsIndex.description')}
                  </p>
                </div>

                {/* Bulk Actions Dialog */}
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>{t('systemSecretsIndex.bulkActionsButton')}</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>{t('systemSecretsIndex.dialog.title')}</DialogTitle>
                      <DialogDescription>
                        {t('systemSecretsIndex.dialog.description')}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleBulkAction} className="space-y-4 mt-4">
                      <RadioGroup
                        value={bulkAction}
                        onValueChange={(value) => setBulkAction(value as 'set' | 'delete')}
                        className="flex flex-col gap-3"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="set" id="action-set" />
                          <Label htmlFor="action-set" className="cursor-pointer">
                            {t('systemSecretsIndex.dialog.setForAll')}
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="delete" id="action-delete" />
                          <Label htmlFor="action-delete" className="cursor-pointer">
                            {t('systemSecretsIndex.dialog.deleteFromAll')}
                          </Label>
                        </div>
                      </RadioGroup>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="bulk-key">{t('systemSecretsIndex.dialog.keyLabel')}</Label>
                          <Input
                            id="bulk-key"
                            type="text"
                            placeholder={t('systemSecretsIndex.dialog.keyPlaceholder')}
                            value={bulkKey}
                            onChange={(e) => setBulkKey(e.target.value)}
                            required
                            className="font-mono"
                          />
                        </div>

                        {bulkAction === 'set' && (
                          <div className="space-y-2">
                            <Label htmlFor="bulk-value">{t('systemSecretsIndex.dialog.valueLabel')}</Label>
                            <Input
                              id="bulk-value"
                              type="text"
                              placeholder={t('systemSecretsIndex.dialog.valuePlaceholder')}
                              value={bulkValue}
                              onChange={(e) => setBulkValue(e.target.value)}
                              required
                              className="font-mono"
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 justify-end pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsDialogOpen(false)}
                        >
                          {t('systemSecretsIndex.dialog.cancel')}
                        </Button>
                        <Button
                          type="submit"
                          variant={bulkAction === 'delete' ? 'destructive' : 'default'}
                        >
                          {bulkAction === 'set' ? t('systemSecretsIndex.dialog.setButton') : t('systemSecretsIndex.dialog.deleteButton')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* System Secrets Table */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="text-lg font-semibold text-foreground">{t('systemSecretsIndex.table.title')}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('systemSecretsIndex.table.count', { count: pagy.count })}
                  </p>
                </div>

                {system_secrets.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('systemSecretsIndex.table.key')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('systemSecretsIndex.table.value')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('systemSecretsIndex.table.lastUpdated')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-card">
                        {devInstances.map((instance) => (
                          <>
                            {/* Dev Instance Header Row */}
                            <tr key={`header-${instance.dev_instance.id}`} className="bg-muted/50">
                              <td colSpan={3} className="px-6 py-3">
                                <div className="flex items-center justify-between">
                                  <Link
                                    href={`/admin/dev_instances/${instance.dev_instance.id}`}
                                    className="text-sm font-semibold text-primary hover:underline"
                                  >
                                    {instance.dev_instance.name}
                                  </Link>
                                  <Link
                                    href={`/admin/dev_instances/${instance.dev_instance.id}/system_secrets`}
                                    className="text-xs text-primary hover:underline"
                                  >
                                    {t('systemSecretsIndex.table.manage')}
                                  </Link>
                                </div>
                              </td>
                            </tr>
                            {/* Secret Rows */}
                            {instance.secrets.map((secret) => (
                              <tr key={secret.id} className="hover:bg-muted/30 border-t border-border">
                                <td className="px-6 py-3">
                                  <span className="text-sm font-medium text-foreground font-mono">
                                    {secret.key}
                                  </span>
                                </td>
                                <td className="px-6 py-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground font-mono">
                                      {visibleValues.has(secret.id) ? secret.value : '••••••••'}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => toggleValueVisibility(secret.id)}
                                      className="text-gray-400 hover:text-muted-foreground"
                                    >
                                      {visibleValues.has(secret.id) ? (
                                        <EyeOff className="h-4 w-4" />
                                      ) : (
                                        <Eye className="h-4 w-4" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                                <td className="px-6 py-3">
                                  <span className="text-sm text-muted-foreground">
                                    {new Date(secret.updated_at).toLocaleDateString()}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-6 py-8">
                    <p className="text-sm text-muted-foreground text-center">
                      {t('systemSecretsIndex.table.empty')}
                    </p>
                  </div>
                )}

                {/* Pagination */}
                {pagy.pages > 1 && (
                  <div className="px-6 py-4 border-t border-border flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {t('systemSecretsIndex.pagination.showing', { from: pagy.from, to: pagy.to, count: pagy.count })}
                    </div>
                    <div className="flex gap-2">
                      {pagy.prev && (
                        <Link
                          href={`/admin/system_secrets?page=${pagy.prev}`}
                          className="px-3 py-1 text-sm border border-border rounded hover:bg-muted"
                        >
                          {t('systemSecretsIndex.pagination.previous')}
                        </Link>
                      )}
                      <span className="px-3 py-1 text-sm">
                        {t('systemSecretsIndex.pagination.page', { current: pagy.page, total: pagy.pages })}
                      </span>
                      {pagy.next && (
                        <Link
                          href={`/admin/system_secrets?page=${pagy.next}`}
                          className="px-3 py-1 text-sm border border-border rounded hover:bg-muted"
                        >
                          {t('systemSecretsIndex.pagination.next')}
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
